/**
 * Referrals Page
 * Manage customer referral program and track performance
 *
 * Features:
 * - Referral program configuration
 * - Performance analytics
 * - Referral leaderboard
 * - Shareable referral links
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, toast } from '../components/ui';
import { referralsApi, ReferralConfig, ReferralLeaderboardEntry, ReferralOverview, handleApiError } from '../lib/api';

export const ReferralsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'settings' | 'leaderboard'>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [config, setConfig] = useState<ReferralConfig | null>(null);
  const [overview, setOverview] = useState<ReferralOverview | null>(null);
  const [leaderboard, setLeaderboard] = useState<ReferralLeaderboardEntry[]>([]);

  // Local editable config state
  const [referrerReward, setReferrerReward] = useState(50);
  const [refereeReward, setRefereeReward] = useState(50);
  const [bonusMultiplier, setBonusMultiplier] = useState(1);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [configRes, overviewRes, leaderboardRes] = await Promise.allSettled([
        referralsApi.getConfig(),
        referralsApi.getOverview(),
        referralsApi.getLeaderboard(10),
      ]);

      if (configRes.status === 'fulfilled') {
        const c = configRes.value.data.data;
        setConfig(c);
        setReferrerReward(c.referrerReward);
        setRefereeReward(c.refereeReward);
        setBonusMultiplier(c.bonusMultiplier);
        setEnabled(c.enabled);
      }

      if (overviewRes.status === 'fulfilled') {
        setOverview(overviewRes.value.data.data);
      }

      if (leaderboardRes.status === 'fulfilled') {
        setLeaderboard(leaderboardRes.value.data.data);
      }
    } catch (error) {
      console.error('Failed to load referral data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);
    if (referrerReward < 0 || refereeReward < 0) {
      setSaveError('Reward amounts cannot be negative.');
      return;
    }
    if (bonusMultiplier < 1) {
      setSaveError('Bonus multiplier must be at least 1.');
      return;
    }
    setIsSaving(true);
    try {
      await referralsApi.updateConfig({ referrerReward, refereeReward, bonusMultiplier });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      const apiError = handleApiError(error);
      setSaveError(apiError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const conversionRate = overview && overview.totalReferrals > 0
    ? Math.round((overview.completedReferrals / overview.totalReferrals) * 100)
    : 0;

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-black">Referral Program</h1>
          <p className="text-base mt-2 text-black-lighter">
            Turn your customers into brand ambassadors
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" size="md" onClick={() => toast.info('Referral report export coming soon.')}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export Report
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="inline-flex rounded-lg p-1 self-start bg-cream-100 border border-border-light">
        {(['overview', 'settings', 'leaderboard'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
              activeTab === tab
                ? 'bg-white text-black shadow-sm'
                : 'bg-transparent text-black-lighter hover:text-black'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-black-lighter">Total Referrals</p>
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-h2 font-bold text-black">{isLoading ? '—' : (overview?.totalReferrals ?? 0)}</p>
                <p className="text-xs text-black-lighter mt-1">{overview?.pendingReferrals ?? 0} pending</p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-black-lighter">Conversion Rate</p>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-h2 font-bold text-black">{isLoading ? '—' : `${conversionRate}%`}</p>
                <p className="text-xs text-black-lighter mt-1">{overview?.completedReferrals ?? 0} completed</p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-black-lighter">Stars Rewards Issued</p>
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-h2 font-bold text-black">{isLoading ? '—' : (overview?.totalRewardsIssued ?? 0).toLocaleString()}</p>
                <p className="text-xs text-black-lighter mt-1">Lifetime rewards</p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-black-lighter">Referrer Reward</p>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <p className="text-h2 font-bold text-black">{isLoading ? '—' : (config?.referrerReward ?? referrerReward)}</p>
                <p className="text-xs text-black-lighter mt-1">Stars per referral</p>
              </div>
            </Card>
          </div>

          {/* How It Works */}
          <Card>
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-black">How Referrals Work</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Your customers share their unique referral code or link with friends
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">1️⃣</span>
                  </div>
                  <h4 className="text-base font-semibold text-black mb-2">Customer Shares</h4>
                  <p className="text-sm text-black-lighter">
                    Existing customer shares their unique referral code or link
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">2️⃣</span>
                  </div>
                  <h4 className="text-base font-semibold text-black mb-2">Friend Signs Up</h4>
                  <p className="text-sm text-black-lighter">
                    New customer creates account using the referral code
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">3️⃣</span>
                  </div>
                  <h4 className="text-base font-semibold text-black mb-2">First Purchase</h4>
                  <p className="text-sm text-black-lighter">
                    New customer makes their first qualifying purchase
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">✅</span>
                  </div>
                  <h4 className="text-base font-semibold text-black mb-2">Both Get Rewarded</h4>
                  <p className="text-sm text-black-lighter">
                    Referrer gets {config?.referrerReward ?? referrerReward} Stars, referee gets {config?.refereeReward ?? refereeReward} Stars
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="max-w-3xl">
          <Card>
            <div className="p-6 border-b border-border-light">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-h4 font-semibold text-black">Referral Program Settings</h3>
                  <p className="text-sm mt-1 text-black-lighter">
                    Configure rewards and requirements for your referral program
                  </p>
                </div>
                <label className="flex items-center gap-3">
                  <span className="text-base text-black-lighter">Program Status</span>
                  <div className="relative inline-block w-12 h-6">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-300 rounded-full peer peer-checked:bg-orange-400 transition-colors"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6"></div>
                  </div>
                </label>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Rewards */}
              <div>
                <h4 className="text-base font-semibold text-black mb-4">Reward Amounts</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Referrer Reward
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={referrerReward}
                        onChange={(e) => setReferrerReward(Number(e.target.value))}
                        min="0"
                        step="10"
                        className="flex-1 px-3 py-2.5 bg-white border border-border-light rounded-lg text-base text-black focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                      <span className="text-base text-black-lighter">Stars</span>
                    </div>
                    <p className="text-xs text-black-lighter mt-1">
                      Reward for the customer who refers
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Referee Reward
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={refereeReward}
                        onChange={(e) => setRefereeReward(Number(e.target.value))}
                        min="0"
                        step="10"
                        className="flex-1 px-3 py-2.5 bg-white border border-border-light rounded-lg text-base text-black focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                      <span className="text-base text-black-lighter">Stars</span>
                    </div>
                    <p className="text-xs text-black-lighter mt-1">
                      Welcome bonus for the new customer
                    </p>
                  </div>
                </div>
              </div>

              {/* Bonus Multiplier */}
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Bonus Multiplier
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={bonusMultiplier}
                    onChange={(e) => setBonusMultiplier(Number(e.target.value))}
                    min="1"
                    step="0.1"
                    className="w-32 px-3 py-2.5 bg-white border border-border-light rounded-lg text-base text-black focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <span className="text-base text-black-lighter">x multiplier on rewards</span>
                </div>
                <p className="text-xs text-black-lighter mt-2">
                  Multiply base rewards for special promotions (1 = no multiplier)
                </p>
              </div>

              {/* Sharing Options */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-blue-900 mb-2">Customer Sharing Options</p>
                    <p className="text-sm text-blue-800">
                      Customers can share their referral link via SMS, email, WhatsApp, Instagram, Facebook, or copy their unique referral code directly.
                    </p>
                  </div>
                </div>
              </div>

              {saveError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{saveError}</p>
                </div>
              )}

              {saveSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">Settings saved successfully.</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-border-light">
              <Button
                variant="primary"
                size="md"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Referral Settings'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="max-w-4xl">
          <Card>
            <div className="p-6 border-b border-border-light">
              <h3 className="text-h4 font-semibold text-black">Top Referrers</h3>
              <p className="text-sm mt-1 text-black-lighter">
                Customers who are driving the most referrals
              </p>
            </div>
            <div className="p-6 space-y-4">
              {isLoading ? (
                <p className="text-sm text-black-lighter text-center py-8">Loading leaderboard...</p>
              ) : leaderboard.length === 0 ? (
                <p className="text-sm text-black-lighter text-center py-8">No referral data yet. Share your program with customers to get started!</p>
              ) : (
                leaderboard.map((referrer, index) => (
                  <div
                    key={referrer.customerId}
                    className="flex items-center gap-4 p-5 bg-cream-50 rounded-lg hover:bg-cream-100 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                          index === 0
                            ? 'bg-yellow-100 text-yellow-700'
                            : index === 1
                            ? 'bg-gray-200 text-gray-700'
                            : index === 2
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-cream-200 text-black-lighter'
                        }`}
                      >
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-semibold text-black">{referrer.customerName}</p>
                      <p className="text-sm text-black-lighter">{referrer.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-orange-400">{referrer.totalReferrals}</p>
                      <p className="text-xs text-black-lighter">Referrals</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">{referrer.completedReferrals}</p>
                      <p className="text-xs text-black-lighter">Completed</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-purple-600">{referrer.totalRewardsEarned}</p>
                      <p className="text-xs text-black-lighter">Stars Earned</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Rewards Info */}
          <Card className="mt-6 bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
            <div className="p-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-400 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-orange-900 mb-2">Recognize Your Top Referrers</h4>
                  <p className="text-sm text-orange-800">
                    Consider sending personalized thank you messages or exclusive offers to your top referrers. They're your biggest brand advocates!
                  </p>
                  <div className="mt-4">
                    <Button variant="primary" size="sm" onClick={() => toast.info('Bulk messaging to referrers coming soon.')}>
                      Send Thank You Message
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReferralsPage;
